import type { Brand } from "./brands";

/**
 * Brand lockup for the portal header and sign-in panel. Recursive draws its mark
 * from the design system (the nested-gable SVG, matching the staff header);
 * Solid Earth and Nautilent use their real white wordmark artwork — never a
 * typeset substitute (COMPONENTS.md BrandHeader). `on` is the on-surface ink.
 *
 * `variant="header"` is the compact 56px-bar lockup; `variant="signin"` is the
 * larger sign-in-panel lockup.
 */
export function BrandMark({
  brand,
  on,
  variant,
}: {
  brand: Brand;
  on: string;
  variant: "header" | "signin";
}) {
  const header = variant === "header";
  if (brand.mark === "gable") {
    const s = header ? 27 : 34;
    return (
      <span style={{ display: "flex", alignItems: "center", gap: header ? 11 : 13 }}>
        <svg width={s} height={s} viewBox="0 0 100 100" style={{ flex: "none" }}>
          <rect x="13" y="13" width="74" height="74" rx="2" fill="none" stroke={on} strokeWidth="6" />
          <g transform="translate(50,50) scale(0.78) translate(-50,-52)">
            <path
              d="M18 80 A8 8 0 0 1 34 80 A8 8 0 0 1 50 80 A8 8 0 0 1 66 80 A8 8 0 0 1 82 80 M26 64 A8 8 0 0 1 42 64 A8 8 0 0 1 58 64 A8 8 0 0 1 74 64 M34 48 A8 8 0 0 1 50 48 A8 8 0 0 1 66 48 M42 32 A8 8 0 0 1 58 32"
              fill="none"
              stroke={on}
              strokeWidth="7"
              strokeLinecap="round"
            />
          </g>
        </svg>
        <span style={{ fontWeight: 600, fontSize: header ? 17 : 23, letterSpacing: header ? "-0.5px" : "-0.8px", color: on }}>
          {brand.name}
        </span>
      </span>
    );
  }
  if (brand.mark === "globe") {
    // Solid Earth: mark + wordmark artwork. The wide wordmark reads better in the header bar.
    return (
      <span style={{ display: "flex", alignItems: "center", gap: header ? 5 : 13 }}>
        <img src="/brand/solid-earth-mark-white.png" alt="" style={{ height: header ? 24 : 52, width: "auto", display: "block", flex: "none" }} />
        <img
          src={header ? "/brand/solid-earth-wordmark-wide-white.png" : "/brand/solid-earth-wordmark-white.png"}
          alt="Solid Earth"
          style={{ height: header ? 19 : 46, width: "auto", display: "block", flex: "none" }}
        />
      </span>
    );
  }
  // Nautilent: sunburst mark + wordmark artwork.
  return (
    <span style={{ display: "flex", alignItems: "center", gap: header ? 3 : 13 }}>
      <img src="/brand/nautilent-mark-white.png" alt="" style={{ height: header ? 21 : 34, width: "auto", display: "block", flex: "none" }} />
      <img src="/brand/nautilent-wordmark-white.png" alt="Nautilent" style={{ height: header ? 15 : 25, width: "auto", display: "block", flex: "none" }} />
    </span>
  );
}
